import { PhoneNumber } from "@/Common/Api/Contacts/Dto/PhoneNumber";
import { LuxonDateHelper } from "@/Common/Helpers/LuxonDateHelper";
import { ContactPointType } from "@/Common/Models/Contacts/Enums/ContactPointType";
import { PhoneType } from "@/Common/Models/Contacts/Enums/PhoneType";
import { Locale } from "@/Common/Models/Localization/Locale";
import { Patient } from "@/Common/Models/Patient/Patient";
import { RelationshipLinks } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";
import { OntarioObstetricalFileFieldLimits } from "@/Patient/Components/MedicalNote/ObstetricalFile/Form/Ontario/OntarioObstetricalFileFieldLimits";
import { MedicalItemScreeningTest } from "@/Patient/Models/MedicalSummary/MedicalItemScreeningTest";
import { BaseObstetricalFileModel } from "@/Patient/Models/ObstetricalFile/BaseObstetricalFileModel";
import { ObstetricalHistoryItem } from "@/Patient/Models/ObstetricalFile/ObstetricalHistoryItem";
import { OntarioObstetricalFileForm } from "@/Patient/Models/ObstetricalFile/Ontario/OntarioObstetricalFileForm";
import { OntarioObstetricalFileFormCommonData } from "@/Patient/Models/ObstetricalFile/Ontario/OntarioObstetricalFileFormCommonData";
import { OntarioObstetricalFileFormOpr1 } from "@/Patient/Models/ObstetricalFile/Ontario/OntarioObstetricalFileFormOpr1";
import { OntarioObstetricalFileFormOpr2 } from "@/Patient/Models/ObstetricalFile/Ontario/OntarioObstetricalFileFormOpr2";
import { OntarioObstetricalPatientPartnerInformation } from "@/Patient/Models/ObstetricalFile/Ontario/OPR1/OntarioObstetricalPatientPartnerInformation";
import { OntarioObstetricalPatientPersonalInformation } from "@/Patient/Models/ObstetricalFile/Ontario/OPR1/OntarioObstetricalPatientPersonalInformation";
import { OntarioObstetricalPregnancySummary } from "@/Patient/Models/ObstetricalFile/Ontario/OPR1/OntarioObstetricalPregnancySummary";
import { ActivityPlanning } from "@/Patient/Models/ObstetricalFile/Ontario/Postnatal/ActivityPlanning";
import { OntarioObstetricalPostNatalHistory } from "@/Patient/Models/ObstetricalFile/Ontario/Postnatal/OntarioObstetricalPostNatalHistory";
import { VaginalExperience } from "@/Patient/Models/ObstetricalFile/Ontario/Postnatal/VaginalExperience";
import { BaseObstetricalFormCreator, ObstetricalFileParams } from "../BaseObstetricalFormCreator";

class OntarioObstetricalFileFormCreator extends BaseObstetricalFormCreator {
    public createObstetricalFileForm(params?: ObstetricalFileParams): OntarioObstetricalFileForm {
        // Family Medecine specialty.
        const physician = params?.patient.IntervenorsOnFile.firstOrDefault(p =>
            p.Model.LicenceOrganisationSpecialisation?.SpecialisationCode == "0"
            || p.Model.LicenceOrganisationSpecialisation?.NameEn === "Family Medicine"
        );

        const previousForm = params.previousObstetricalFile?.getFormObject<OntarioObstetricalFileForm>();
        const latestPapTestMedicalItem = new MedicalItemScreeningTest(params.latestPapTest);

        return new OntarioObstetricalFileForm({
            Context: params.obstetricFormContext,
            ObstetricalCounters: this.createObstetricalCounters(params.obstetricalCounters),
            Opr1: new OntarioObstetricalFileFormOpr1({
                PersonalInformation: this.getPersonalInformation(params.patient),
                PregnancySummary: new OntarioObstetricalPregnancySummary({
                    NeonatalOrChildDeathCount: previousForm?.Opr1?.PregnancySummary?.NeonatalOrChildDeathCount
                }),
                ObstetricalHistory: this.getPreviousObstetricalHistory(params.previousObstetricalFile)
            }),
            Opr2: new OntarioObstetricalFileFormOpr2({
                LastPapDate: params.latestPapTest?.CompletionDate,
                LastPapResult: latestPapTestMedicalItem.getInterpretationLocalizedValue(sessionStore.locale)
            }),
            Common: new OntarioObstetricalFileFormCommonData({
                FamilyPhysicianPrimaryCareProvider: physician?.GetName()
            })
        });
    }

    private getPreviousObstetricalHistory(previousObstetricalFile: BaseObstetricalFileModel): ObstetricalHistoryItem[] {
        if (!previousObstetricalFile)
            return [];

        const previousForm = previousObstetricalFile?.getFormObject<OntarioObstetricalFileForm>();

        if (!previousObstetricalFile.PostnatalDate)
            return previousForm?.Opr1?.ObstetricalHistory ?? [];

        const newRow = new ObstetricalHistoryItem({
            Date: previousForm?.Postnatal?.DateOfDelivery,
            GestationWeeksAtBirth: previousForm?.Postnatal.GestationalAgeAtBirth,
            Comments: previousForm?.Postnatal?.History?.Details?.substr(0, OntarioObstetricalFileFieldLimits.Page1.ObstetricalHistory_Comments.MaxChars),
            BirthWeight: previousForm?.Postnatal?.History?.BirthWeight,
            TypeOfBirth: this.getTypeOfBirth(previousForm?.Postnatal?.History).substr(0, OntarioObstetricalFileFieldLimits.Page1.ObstetricalHistory_TypeOfBirth.MaxChars)
        });

        return [...previousForm?.Opr1?.ObstetricalHistory, newRow];
    }

    private getTypeOfBirth(postnatalHistory: OntarioObstetricalPostNatalHistory): string {
        if (!postnatalHistory)
            return "";

        if (postnatalHistory.CaesareanBirthExperience !== ActivityPlanning.Unknown)
            return cacheStore.GetString("CSectionLabel");

        if (postnatalHistory.VaginalBirthExperienceItem?.length >= 2)
            return "";

        const vaginalBirthItem = postnatalHistory.VaginalBirthExperienceItem.length > 0 ? postnatalHistory.VaginalBirthExperienceItem[0] : null;

        switch (vaginalBirthItem) {
            case VaginalExperience.VaginalBirthAfterCaesarianSection:
                return cacheStore.GetString("VaginalBirthAfterCesareanAbbrevLabel");
            case VaginalExperience.Spontaneous:
                return cacheStore.GetString("SVDLabel");
            case VaginalExperience.Forceps:
                return cacheStore.GetString("ForcepsLabel");
            case VaginalExperience.Vacuum:
                return cacheStore.GetString("VacuumLabel");
            default:
                return "";
        }
    }

    private getPersonalInformation(patient?: Patient): OntarioObstetricalPatientPersonalInformation {
        const spouseLookUp = cacheStore.GetRelationshipTypeLookups()?.firstOrDefault(p => p.LookupCode === RelationshipLinks.Spouse);

        const partner = patient.Relationships.firstOrDefault(p => p.RelationLookupId === spouseLookUp?.LookupID);

        const partnerInfo = partner?.Individual;

        return new OntarioObstetricalPatientPersonalInformation({
            AlternateContact: OntarioObstetricalFileFormCreator.GetAlternateContact(patient),
            PatientFileNumber: patient?.PatientResponse?.Identifier01,
            Language: cacheStore.GetLookupById(patient?.IndividualResponse?.PreferredSpokenLanguageLookupId)?.GetValue(Locale.en),
            Occupation: patient?.PatientResponse?.Profession,
            PartnerInformation: new OntarioObstetricalPatientPartnerInformation({
                LastName: partnerInfo?.LastName,
                FirstName: partnerInfo?.FirstName,
                Age: LuxonDateHelper.getYearsSince(partnerInfo?.DateOfBirth),
                Occupation: partner?.Patient?.Profession
            }),
            RelationshipStatus: cacheStore.GetLookupById(patient?.IndividualResponse?.MaritalStatusLookupId)?.GetValue(Locale.en)
        });
    }

    /**
     * Get the alternative contact according to the following priority:
     *  1. Non preferred personal phone
     *  2. Personal Email
     *  3. Non preferred work phone
     *  4. Work email. Cell phones must take priority over Phone type.
     */
    public static GetAlternateContact(patient: Patient): string {
        const personalContactPointTypeId = cacheStore.getLookupByContactPointType(ContactPointType.Personal)?.LookupID;
        const workContactPointTypeId = cacheStore.getLookupByContactPointType(ContactPointType.Work)?.LookupID;
        const phoneTypeId = cacheStore.getLookupByPhoneType(PhoneType.Phone)?.LookupID;
        const mobileTypeId = cacheStore.getLookupByPhoneType(PhoneType.Mobile)?.LookupID;

        const primaryContact = PhoneNumber.GetPrimaryPhoneNumber(patient.Phones);

        const possibleAlternatePhoneContacts = patient.Phones?.filter(p => (!primaryContact?.ContactPoint_id || p.ContactPoint_id !== primaryContact?.ContactPoint_id) && !p.IsPreferred);

        const personalPhone = possibleAlternatePhoneContacts.firstOrDefault(p => p.ContactPointTypeLookupId === personalContactPointTypeId && p.PhoneTypeLookupId === mobileTypeId)
            ?? possibleAlternatePhoneContacts.firstOrDefault(p => p.ContactPointTypeLookupId === personalContactPointTypeId && p.PhoneTypeLookupId === phoneTypeId);
        if (personalPhone)
            return PhoneNumber.stringFormattedPhone(personalPhone);

        const personalEmail = patient.Emails.firstOrDefault(p => p.ContactPointTypeLookupId === personalContactPointTypeId);
        if (personalEmail)
            return personalEmail.Address;

        const workPhone = possibleAlternatePhoneContacts.firstOrDefault(p => p.ContactPointTypeLookupId === workContactPointTypeId && p.PhoneTypeLookupId === mobileTypeId)
            ?? possibleAlternatePhoneContacts.firstOrDefault(p => p.ContactPointTypeLookupId === workContactPointTypeId && p.PhoneTypeLookupId === phoneTypeId);
        if (workPhone)
            return PhoneNumber.stringFormattedPhone(workPhone);

        const workEmail = patient.Emails.firstOrDefault(p => p.ContactPointTypeLookupId === workContactPointTypeId);
        if (workEmail)
            return workEmail.Address;

        return "";
    }
}

export { OntarioObstetricalFileFormCreator };
